import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { defaultQueryParams, QueryParams } from 'api/_legacy/types'
import {
  ApiAssetDefaultConfiguration,
  ApiCashInstrumentApiPaginationResult,
  ApiDeposit,
  ApiLoan,
  ApiZcbCurve
} from 'api/rtk-api'
import { all, call, put, PutEffect, select, takeLatest } from 'redux-saga/effects'
import { RootState } from 'state/store'

import api from '@/api/_legacy'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { Errors, extractErrorMessagesFromServerResponseToArray } from '../../tradebuilder-v2/util'
import { validate } from './validation-rules'

const TENANT_ID = 'absa'

export type PageType = 'Deposit' | 'Loan'

interface CashInstrumentQueryParams extends QueryParams {
  CashInstrumentType: PageType
}

export type EditCashInstrumentsState = {
  availableCurrencies: string[]
  availableRefRates: string[]
  canProceed: boolean
  currentPageNumber: number
  errors: Errors
  instruments: (ApiDeposit | ApiLoan | ApiZcbCurve)[]
  isNew: boolean
  numTotalInstruments: number
  pageType?: PageType
  query: CashInstrumentQueryParams
  saveSuccess: boolean
  selectedInstrument?: ApiDeposit | ApiLoan | ApiZcbCurve
  serverErrors?: string[]
}

const initialState: EditCashInstrumentsState = {
  canProceed: false,
  saveSuccess: false,
  numTotalInstruments: 0,
  currentPageNumber: 1,
  instruments: [],
  availableCurrencies: [],
  availableRefRates: [],
  isNew: false,
  errors: {},
  query: { ...defaultQueryParams, CashInstrumentType: 'Deposit' }
}

export const editCashInstrumentsSlice = createSlice({
  name: 'editCashInstruments',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<PageType>) => {
      state.pageType = action.payload
      state.query.CashInstrumentType = state.pageType
      state.selectedInstrument = undefined
    },
    saveError: (state, action: PayloadAction<any>) => {
      state.serverErrors = extractErrorMessagesFromServerResponseToArray(action.payload)
    },
    setSaveSuccessFalse: state => {
      state.saveSuccess = false
    },
    fetchInstrumentDetailSuccess: (
      state,
      action: PayloadAction<ApiCashInstrumentApiPaginationResult>
    ) => {
      state.numTotalInstruments = action.payload.totalDocuments
      state.instruments = action.payload.data
    },
    fetchDefaultsSuccess: (state, action: PayloadAction<ApiAssetDefaultConfiguration>) => {
      state.availableCurrencies = action.payload.currencies
    },
    fetchReferenceRatesSuccess: (state, action: PayloadAction<string[]>) => {
      state.availableRefRates = action.payload
    },
    search: (state, action: PayloadAction<string>) => {
      state.query.WildcardString = action.payload
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.query.PageNumber = action.payload
    },
    clearFilters: state => {
      state.query.WildcardString = ''
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    saveInstrument: () => {
      // eslint-disable-next-line no-console
      console.log('saving instrument test...')
    },
    saveInstrumentSuccess: state => {
      state.canProceed = false
      state.isNew = false
      state.saveSuccess = true
    },
    setSelectedInstrument: (state, action: PayloadAction<string>) => {
      state.selectedInstrument = state.instruments.find(x => x.instrumentId === action.payload)
      state.isNew = false
      state.saveSuccess = false
      state.errors = {}
    },
    setIsNew: state => {
      state.isNew = true
      state.saveSuccess = false
      state.errors = {}
      state.selectedInstrument = {
        type: state.pageType,
        instrumentId: '',
        name: '',
        currencyIso: state.availableCurrencies[0],
        referenceRate: state.availableRefRates[0],
        referenceRateSpread: 0,
        priceScalingFactor: 1,
        sharePrecisionScale: 6 // Check this
      } as ApiDeposit | ApiLoan
    },
    setName: (state, action: PayloadAction<string>) => {
      state.selectedInstrument.name = action.payload
      delete state.errors.instrument_name
      state.canProceed = true
      state.saveSuccess = false
    },
    setId: (state, action: PayloadAction<string>) => {
      state.selectedInstrument.instrumentId = action.payload
      delete state.errors.id
      state.canProceed = true
      state.saveSuccess = false
    },
    setCurrency: (state, action: PayloadAction<string>) => {
      state.selectedInstrument.currencyIso = action.payload
      state.canProceed = true
      state.saveSuccess = false
    },
    setRefRate: (state, action: PayloadAction<string>) => {
      let instrument
      if (state.selectedInstrument.type === 'Deposit') {
        instrument = state.selectedInstrument as ApiDeposit
      }
      if (state.selectedInstrument.type === 'Loan') {
        instrument = state.selectedInstrument as ApiLoan
      }
      ;(instrument as any).referenceRate = action.payload
      state.selectedInstrument = instrument
      state.canProceed = true
      state.saveSuccess = false
    },
    setPriceScalingFactor: (state, action: PayloadAction<number>) => {
      let instrument
      if (state.selectedInstrument.type === 'Deposit') {
        instrument = state.selectedInstrument as ApiDeposit
      }
      if (state.selectedInstrument.type === 'Loan') {
        instrument = state.selectedInstrument as ApiLoan
      }
      ;(instrument as any).priceScalingFactor = action.payload
      state.selectedInstrument = instrument
      state.canProceed = true
      state.saveSuccess = false
    },
    setRefRateSpread: (state, action: PayloadAction<{ sign: string; spread: number }>) => {
      let instrument
      if (state.selectedInstrument.type === 'Deposit') {
        instrument = state.selectedInstrument as ApiDeposit
      }
      if (state.selectedInstrument.type === 'Loan') {
        instrument = state.selectedInstrument as ApiLoan
      }
      const { spread } = action.payload
      ;(instrument as any).referenceRateSpread = action.payload.sign === '-' ? spread * -1 : spread
      state.selectedInstrument = instrument
      state.canProceed = true
      state.saveSuccess = false
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const { actions } = editCashInstrumentsSlice
export type EditCashInstrumentsActions = typeof actions

function saveInstrumentSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* saveInstrument2Saga() {
    const state: RootState = yield select()
    const { editCashInstruments } = state
    const { selectedInstrument } = editCashInstruments

    const result = validate(editCashInstruments)
    if (!result.isValid) {
      yield put(actions.setInvalid(result.errors))
    } else {
      if (!selectedInstrument.instrumentId) {
        throw new Error('FATAL ERROR: Asset Id does not exist')
      }
      try {
        if (editCashInstruments.isNew) {
          if (editCashInstruments.pageType === 'Deposit') {
            yield call(api.tenant.depositCreate, TENANT_ID, selectedInstrument as ApiDeposit)
            yield onSuccess
          } else {
            yield call(api.tenant.loanCreate, TENANT_ID, selectedInstrument as ApiLoan)
            yield onSuccess
          }
        } else if (editCashInstruments.pageType === 'Deposit') {
          yield call(api.tenant.depositUpdateCreate, TENANT_ID, selectedInstrument as ApiDeposit)
          yield onSuccess
        } else {
          yield call(api.tenant.loanUpdateCreate, TENANT_ID, selectedInstrument as ApiLoan)
          yield onSuccess
        }
      } catch (error) {
        yield onFail(error)
      }
    }
  }
}

function* fetchInstrumentDetailSaga() {
  const state: RootState = yield select()
  const { editCashInstruments } = state
  const { query } = editCashInstruments
  try {
    const response = yield call(api.tenant.listDetail2, TENANT_ID, query)
    yield put({
      type: 'editCashInstruments/fetchInstrumentDetailSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editCashInstruments/fetchInstrumentDetailFailure', payload: error })
  }
}

function* fetchAssetDefaultsSaga() {
  try {
    const response = yield call(api.tenant.defaultsDetail, TENANT_ID)
    yield put({
      type: 'editCashInstruments/fetchDefaultsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editCashInstruments/fetchDefaultsFailure', payload: error })
  }
}

function* fetchReferenceRatesSaga() {
  try {
    const response = yield call(api.tenant.cashinstrumentreferenceratesDetail, TENANT_ID)
    yield put({
      type: 'editCashInstruments/fetchReferenceRatesSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editCashInstruments/fetchReferenceRatesFailure', payload: error })
  }
}

function* fetchInitialDataSaga() {
  yield all([fetchInstrumentDetailSaga(), fetchAssetDefaultsSaga(), fetchReferenceRatesSaga()])
}

export function* editCashInstrumentRootSaga() {
  yield all([
    takeLatest('editCashInstruments/init', fetchInitialDataSaga),
    takeLatest('editCashInstruments/search', fetchInstrumentDetailSaga),
    takeLatest('editCashInstruments/saveInstrumentSuccess', fetchInstrumentDetailSaga),
    takeLatest(
      'editCashInstruments/saveInstrument',
      saveInstrumentSaga(put(actions.saveInstrumentSuccess()), e => put(actions.saveError(e)))
    )
  ])
}

export default editCashInstrumentsSlice.reducer
